import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Col, Row } from 'react-bootstrap';
import { CustomButton, DateTimePicker, Textfield } from '../../_common';
import { isValidDate } from '../../../utils/helpers';
import './JobMetrics.scss';

export interface JobMetricValueSubmit {
    fromDate: Date;
    toDate: Date;
    value: number;
}

interface JobMetricEntryModalProps {
    show: boolean;
    title: string;
    fieldLabel: string;
    savingLabel?: string;
    onClickHide: () => void;
    onClickSave: (payload: JobMetricValueSubmit) => Promise<void> | void;
}

interface FieldErrors {
    fromDateError: string;
    toDateError: string;
    valueError: string;
}

const initialErrors: FieldErrors = {
    fromDateError: '',
    toDateError: '',
    valueError: '',
};

const isDateBeforeToday = (date: Date): boolean =>
    new Date(date.toDateString()) < new Date(new Date().toDateString());

const JobMetricEntryModal = ({
    show,
    title,
    fieldLabel,
    savingLabel,
    onClickHide,
    onClickSave,
}: JobMetricEntryModalProps): React.ReactElement => {
    const [fromDate, setFromDate] = useState<Date>(new Date(''));
    const [toDate, setToDate] = useState<Date>(new Date(''));
    const [valueStr, setValueStr] = useState<string>('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>(initialErrors);
    const [isSaving, setIsSaving] = useState(false);

    const clearState = (): void => {
        setFromDate(new Date(''));
        setToDate(new Date(''));
        setValueStr('');
        setFieldErrors(initialErrors);
        setIsSaving(false);
    };

    useEffect(() => {
        if (!show) clearState();
    }, [show]);

    const onCancel = (): void => {
        clearState();
        onClickHide();
    };

    const handleFromDateChange = (value: Date): void => {
        const dateValue = !!value ? new Date(value) : new Date('');
        setFromDate(dateValue);
        let fromDateError = '';
        if (!isValidDate(dateValue)) fromDateError = 'Please enter a valid date.';
        else if (isDateBeforeToday(dateValue)) fromDateError = 'Start date should not be a previous date.';
        else if (isValidDate(toDate) && dateValue > toDate)
            fromDateError = 'Start date should not be greater than end date.';
        setFieldErrors((prev) => ({
            ...prev,
            fromDateError,
            toDateError:
                isValidDate(toDate) && isValidDate(dateValue) && dateValue <= toDate ? '' : prev.toDateError,
        }));
    };

    const handleToDateChange = (value: Date): void => {
        const dateValue = !!value ? new Date(value) : new Date('');
        setToDate(dateValue);
        let toDateError = '';
        if (!isValidDate(dateValue)) toDateError = 'Please enter a valid date.';
        else if (isValidDate(fromDate) && dateValue < fromDate)
            toDateError = 'End date should not be less than start date.';
        setFieldErrors((prev) => ({
            ...prev,
            toDateError,
            fromDateError:
                isValidDate(fromDate) && isValidDate(dateValue) && fromDate <= dateValue
                    ? prev.fromDateError.startsWith('Start date should not be greater')
                        ? ''
                        : prev.fromDateError
                    : prev.fromDateError,
        }));
    };

    const handleValueChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ): void => {
        const raw = event.target.value ?? '';
        setValueStr(raw);
        let valueError = '';
        if (raw === '') valueError = '';
        else if (!/^\d{1,5}$/.test(raw)) valueError = 'Enter a whole number between 0 and 99999.';
        setFieldErrors((prev) => ({ ...prev, valueError }));
    };

    const isSaveDisabled =
        isSaving ||
        !isValidDate(fromDate) ||
        !isValidDate(toDate) ||
        valueStr === '' ||
        !!fieldErrors.fromDateError ||
        !!fieldErrors.toDateError ||
        !!fieldErrors.valueError;

    const onSave = async (): Promise<void> => {
        const numeric = parseInt(valueStr, 10);
        if (!Number.isFinite(numeric) || numeric < 0) {
            setFieldErrors((prev) => ({ ...prev, valueError: 'Enter a whole number between 0 and 99999.' }));
            return;
        }
        setIsSaving(true);
        try {
            await onClickSave({ fromDate, toDate, value: numeric });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onCancel} dialogClassName="JobMetricsModal-Container">
            <Modal.Header className="JobMetricsModal-Header">
                <Modal.Title className="JobMetricsModal-Header-Container">
                    <button className="material-icons JobMetricsModal-X-Button" onClick={onCancel}>
                        close
                    </button>
                    <div className="JobMetricsModal-Title">{title}</div>
                </Modal.Title>
            </Modal.Header>

            <div className="JobMetricsModal-FormContainer">
                <Row>
                    <Col md={6}>
                        <DateTimePicker
                            dateOnly
                            label="START DATE"
                            value={fromDate}
                            onChange={(value: any) => handleFromDateChange(value)}
                            error={fieldErrors.fromDateError || ''}
                            required
                        />
                    </Col>
                    <Col md={6}>
                        <DateTimePicker
                            dateOnly
                            label="END DATE"
                            value={toDate}
                            onChange={(value: any) => handleToDateChange(value)}
                            error={fieldErrors.toDateError || ''}
                            required
                        />
                    </Col>
                </Row>
                <Row className="JobMetricsModal-ValueRow">
                    <Col md={6}>
                        <Textfield
                            label={fieldLabel}
                            value={valueStr}
                            onChange={handleValueChange}
                            error={!!fieldErrors.valueError}
                            helperText={fieldErrors.valueError}
                        />
                    </Col>
                </Row>
                <div className="JobMetricsModal-Note">
                    This value applies within the selected date range until a new entry supersedes it.
                </div>
            </div>

            <Modal.Footer className="JobMetricsModal-Footer">
                <CustomButton title="Cancel" isBordered onClick={onCancel}></CustomButton>
                <CustomButton
                    title={isSaving ? savingLabel ?? 'Saving...' : 'Save'}
                    isDisabled={isSaveDisabled}
                    onClick={onSave}
                ></CustomButton>
            </Modal.Footer>
        </Modal>
    );
};

export default JobMetricEntryModal;
